import { useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { useApi } from "../../../hooks/useApi";
import { useAuth } from "../../../contexts/AuthContext";
import { presetToRange } from "../components/DateRange";
import { getTeamStats } from "../../../services/goalsPerformanceApi";
import { getOrgGmailStatus } from "../../../modules/email-insights/engine/backendClient";

const PAGE_SIZE = 8;
// Admins oversee the whole org, so they open on "All team members" (member =
// null). Everyone else opens on THEIR OWN hours: a non-admin's null-scope is
// self + subordinates, which reads as the whole org under the "All team
// members" label, so defaulting them to themselves makes the number match.
const isAdminUser = (u) => u?.role === "org_admin" || u?.role === "main_admin";
// Format a Date as YYYY-MM-DD using LOCAL date parts (not UTC).
// toISOString() would shift to UTC and, in +ve timezones like IST,
// return the previous day for "today" — causing yesterday's data to load.
const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Single hook owning all DashboardV2 server state. Mirrors the original
// Dashboard's API contracts exactly — same endpoints, same params — so the
// numbers are identical, only the presentation changes.
export function useDashboardData() {
  const api = useApi();
  const { user } = useAuth();

  // ── Filters ──
  const [range, setRange] = useState(() => presetToRange("today"));
  const [member, setMember] = useState(() => (isAdminUser(user) ? null : user?.id || null));
  const [project, setProject] = useState(null); // projectId or null = all

  // Auth may resolve AFTER first render (session restore on refresh), so the
  // lazy initialiser above can miss. Seed the default once when the user lands.
  // `memberTouched` flips on the first explicit choice, so if the user picks
  // "All team members" before auth resolves we never overwrite that with self.
  const memberTouched = useRef(!!user?.id);
  useEffect(() => {
    if (memberTouched.current) return;
    if (!user?.id) return;
    memberTouched.current = true;
    // Admin default is already null ("All team members") — nothing to seed.
    if (isAdminUser(user)) return;
    setMember(user.id);
  }, [user]);

  // Wraps setMember so any explicit selection (including "All team members",
  // which sets null) permanently disables the auto-default above.
  const selectMember = useCallback((v) => {
    memberTouched.current = true;
    setMember(v);
  }, []);

  // ── Filter option lists ──
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);

  // ── KPI summary ──
  const [kpi, setKpi] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);

  // ── Daily trend (for charts) ──
  const [trend, setTrend] = useState([]); // [{ label, score, minutes }]
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendIsWeekly, setTrendIsWeekly] = useState(true); // true = fell back to last-7-days

  // ── Most visited apps/websites (activity from screenshots) ──
  const [mostVisited, setMostVisited] = useState([]);
  const [mostVisitedPage, setMostVisitedPage] = useState(1);
  const [mostVisitedMeta, setMostVisitedMeta] = useState({ total: 0, pages: 1, page: 1 });
  const [mostVisitedLoading, setMostVisitedLoading] = useState(true);

  // ── Goals team stats ──
  const [goals, setGoals] = useState({ teamAverageScore: null, completedGoalsCount: 0, totalGoalsCount: 0, previousMonthChange: null });
  const [goalsLoading, setGoalsLoading] = useState(true);

  // ── Gmail connection status (org-wide) ──
  const [gmail, setGmail] = useState(null); // { connectedCount, notConnectedCount, total, members:[] }
  const [gmailLoading, setGmailLoading] = useState(true);

  // ── Project overview ──
  const [projectRows, setProjectRows] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectMeta, setProjectMeta] = useState({ page: 1, pages: 1, total: 0 });
  const [projectSort, setProjectSort] = useState({ key: "time", dir: "desc" });
  const [projectLoading, setProjectLoading] = useState(true);

  // ── Team analytics ──
  // teamRows is the PAGINATED table (PAGE_SIZE per page). teamAll is the same
  // endpoint asked for the whole roster in one go — the people quadrant plots
  // every person, and page 1 of 8 would have been a lie on that card.
  const [teamAll, setTeamAll] = useState([]);
  const [teamAllLoading, setTeamAllLoading] = useState(true);
  const [teamRows, setTeamRows] = useState([]);
  const [teamPage, setTeamPage] = useState(1);
  const [teamMeta, setTeamMeta] = useState({ page: 1, pages: 1, total: 0 });
  const [teamSort, setTeamSort] = useState({ key: "hours", dir: "desc" });
  const [teamLoading, setTeamLoading] = useState(true);

  const reqId = useRef(0);

  // ── Members dropdown — fire first, lite endpoint (no populate / agent enrich) ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const m = await api.get("/api/v1/users/team-members-dropdown");
        if (!alive) return;
        if (m.data?.success) {
          const list = m.data.data || m.data.members || [];
          setMembers(list.map((u) => ({
            value: u.userId || u._id || u.id,
            label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member",
            sub: u.position || u.email,
          })));
        }
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, [api]);

  // ── Projects dropdown (separate so it never blocks members) ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await api.get("/api/v1/projects/dropdown");
        if (!alive) return;
        if (p.data?.success) {
          const list = p.data.data || p.data.projects || [];
          setProjects(list.map((pr) => ({ value: pr._id || pr.id || pr.projectId, label: pr.name })));
        }
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, [api]);

  // ── KPI fetch ──
  // Hours + teamSize + activeMembers all come from the authoritative hours
  // endpoint (team-hours / today-hours). Do NOT also hit team-analytics-paginated
  // here — that endpoint scans the whole org before paging and was doubling
  // cold-load cost for summary fields already returned by team-hours.
  const fetchKpi = useCallback(async () => {
    setKpiLoading(true);
    const id = ++reqId.current;
    try {
      const qs = new URLSearchParams({ startDate: fmt(range.start), endDate: fmt(range.end) });
      const hoursUrl = member
        ? `/api/v1/live-analytics/today-hours?userId=${member}&${qs.toString()}`
        : `/api/v1/live-analytics/team-hours?${qs.toString()}`;

      const hoursRes = await api.get(hoursUrl).catch(() => null);
      if (id !== reqId.current) return;

      const h = hoursRes?.data?.data || {};

      const totalMinutes = h.totalMinutes || 0;
      let idleMinutes = h.totalIdleTimeMinutes;
      let activeMinutes = h.totalActiveTimeMinutes;
      // today-hours (single member) may not return active/idle split → derive.
      if (idleMinutes == null) idleMinutes = 0;
      if (activeMinutes == null) activeMinutes = Math.max(0, totalMinutes - idleMinutes);

      const teamSize = h.teamSize ?? 0;
      const activeMembers = h.activeMembers ?? 0;

      setKpi({
        totalMinutes, activeMinutes, idleMinutes,
        teamSize: member ? 1 : teamSize,
        activeMembers: member ? 1 : activeMembers,
        totalSessions: h.totalSessions ?? 0,
        utilization: totalMinutes ? Math.round((activeMinutes / totalMinutes) * 100) : 0,
        hoursSpark: [],
        scoreSpark: [],
      });
    } catch (_) {
      if (id === reqId.current) setKpi(null);
    } finally {
      if (id === reqId.current) setKpiLoading(false);
    }
  }, [api, range, member]);

  // ── Project overview fetch ──
  const fetchProjects = useCallback(async () => {
    setProjectLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: fmt(range.start), endDate: fmt(range.end),
        page: String(projectPage), limit: String(PAGE_SIZE),
        sortOrder: projectSort.dir,
      });
      if (member) params.append("userId", member);
      const res = await api.get(`/api/v1/live-analytics/project-overview-paginated?${params.toString()}`);
      const data = res.data?.data || {};
      const rows = (data.projects || []).map((p) => ({
        _id: p.projectId, name: p.name, status: p.status,
        timeSpent: p.timeSpent || "0h 0m", avgSession: p.avgSession || "0h 0m",
        members: p.teamMembers || 0,
        isActive: p.isActive !== undefined ? p.isActive : p.status === "active",
        minutes: p.totalMinutes || 0,
      }));
      setProjectRows(rows);
      setProjectMeta({
        page: data.pagination?.page || projectPage,
        pages: data.pagination?.pages || 1,
        total: data.pagination?.total || rows.length,
      });
    } catch (_) {
      setProjectRows([]);
    } finally {
      setProjectLoading(false);
    }
  }, [api, range, member, projectPage, projectSort.dir]);

  // ── Team analytics fetch ──
  const fetchTeam = useCallback(async () => {
    setTeamLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: fmt(range.start), endDate: fmt(range.end),
        page: String(teamPage), limit: String(PAGE_SIZE),
        sortOrder: teamSort.dir,
      });
      if (member) params.append("userId", member);
      const res = await api.get(`/api/v1/live-analytics/team-analytics-paginated?${params.toString()}`);
      const data = res.data?.data || {};
      const rows = (data.teamAnalytics || []).map((m) => {
        const am = m.activeMinutes || 0, im = m.idleMinutes || 0;
        const f = (mins) => `${Math.floor(mins / 60)}h ${mins % 60}m`;
        return {
          userId: m.userId,
          avatar: m.avatar || null,
          name: `${m.firstName || ""} ${m.lastName || ""}`.trim() || m.email || "Member",
          position: m.position || "Team Member",
          hours: m.hours || "0h 0m",
          activeTime: f(am), idleTime: f(im),
          // Raw numbers alongside the formatted strings — the Pulse people
          // quadrant plots hours against active share, and re-parsing "8h 30m"
          // back out of a display string would be a bug waiting to happen.
          activeMinutes: am, idleMinutes: im, totalMinutes: am + im,
          productivity: m.productivity || 0,
          sessions: m.sessions || 0,
          lastActivity: m.lastActivity || null,
          streak: m.streak || 0,
          badge: m.badge || "",          // rank/achievement label (S5)
          badgeIcon: m.badgeIcon || "",  // rank icon image (S5)
          isCurrentUser: m.isCurrentUser || m.userId === user?.id,
          hasData: (m.sessions || 0) > 0 || (m.totalMinutes || 0) > 0,
        };
      });
      setTeamRows(rows);
      setTeamMeta({
        page: data.pagination?.page || teamPage,
        pages: data.pagination?.pages || 1,
        total: data.pagination?.total || rows.length,
      });
    } catch (_) {
      setTeamRows([]);
    } finally {
      setTeamLoading(false);
    }
  }, [api, range, member, teamPage, teamSort.dir, user]);

  // ── Whole-roster fetch, for the people quadrant ──
  const fetchTeamAll = useCallback(async () => {
    setTeamAllLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: fmt(range.start), endDate: fmt(range.end),
        page: "1", limit: "500", sortOrder: "desc",
      });
      if (member) params.append("userId", member);
      const res = await api.get(`/api/v1/live-analytics/team-analytics-paginated?${params.toString()}`);
      const list = res.data?.data?.teamAnalytics || [];
      setTeamAll(list.map((m) => ({
        userId: m.userId,
        avatar: m.avatar || null,
        name: `${m.firstName || ""} ${m.lastName || ""}`.trim() || m.email || "Member",
        position: m.position || "Team Member",
        activeMinutes: m.activeMinutes || 0,
        idleMinutes: m.idleMinutes || 0,
        totalMinutes: (m.activeMinutes || 0) + (m.idleMinutes || 0),
      })));
    } catch (_) {
      setTeamAll([]);
    } finally {
      setTeamAllLoading(false);
    }
  }, [api, range, member]);

  // ── Daily trend fetch ──
  // "Daily activity" adapts to the selected range so the chart is always a
  // proper, visually-meaningful set of bars:
  //   • range spans < 7 days (Today / Yesterday / short custom) → fall back to
  //     the LAST 7 DAYS, so we never show a single lonely bar.
  //   • range spans ≥ 7 days (7 days / 30 days / This month / wide custom) →
  //     show the selected range's days (capped at 31).
  // ONE call to /live-analytics/daily-trend returns the whole per-day series
  // (minutes + score) in a single aggregation — replaces the old per-day
  // fan-out (was up to ~62 requests). Honours `member`.
  const fetchTrend = useCallback(async () => {
    setTrendLoading(true);
    try {
      const oneDay = 24 * 60 * 60 * 1000;
      const startSel = new Date(range.start); startSel.setHours(0, 0, 0, 0);
      const endSel = new Date(range.end); endSel.setHours(0, 0, 0, 0);
      const spanDays = Math.floor((endSel - startSel) / oneDay) + 1; // inclusive

      const isWeekly = spanDays < 7;
      setTrendIsWeekly(isWeekly);

      // Resolve the [winStart, winEnd] window to request.
      let winStart, winEnd;
      if (isWeekly) {
        // Short range → last 7 days ending today.
        winEnd = new Date(); winEnd.setHours(0, 0, 0, 0);
        winStart = new Date(winEnd); winStart.setDate(winEnd.getDate() - 6);
      } else {
        // Selected range (capped at 31 days).
        winStart = new Date(startSel);
        winEnd = new Date(endSel);
        const cap = new Date(winStart); cap.setDate(cap.getDate() + 30);
        if (winEnd > cap) winEnd = cap;
      }

      const p = new URLSearchParams({ startDate: fmt(winStart), endDate: fmt(winEnd) });
      if (member) p.append("userId", member);
      const res = await api.get(`/api/v1/live-analytics/daily-trend?${p.toString()}`);
      const rows = res.data?.data?.series || [];

      const series = rows.map((r) => ({
        label: new Date(r.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" }),
        date: r.date,
        score: Math.round(r.score || 0),
        minutes: Math.round(r.minutes || 0),
      }));
      setTrend(series);
    } catch (_) {
      setTrend([]);
    } finally {
      setTrendLoading(false);
    }
  }, [api, range, member]);

  // ── Most visited fetch (apps/websites from screenshots) ──
  const fetchMostVisited = useCallback(async () => {
    setMostVisitedLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: fmt(range.start), endDate: fmt(range.end),
        page: String(mostVisitedPage), limit: "8",
      });
      if (member) params.append("userId", member);
      const res = await api.get(`/api/v1/live-analytics/activity-from-screenshots?${params.toString()}`);
      const data = res.data?.data || {};
      const list = data.mostVisited || [];
      const pg = data.summary?.pagination || {};
      setMostVisited(list.map((it) => ({
        name: it.name || "Unknown",
        // Match old dashboard: treat "unknown"/empty as no category (hidden), don't force "Other".
        category: it.category && it.category !== "unknown" ? it.category : null,
        type: it.type || "app",
        time: it.formattedTime || it.timeSpent || "0h 0m",
        sessions: it.sessions || 0,
      })));
      setMostVisitedMeta({
        total: pg.total ?? list.length,
        // The API returns `totalPages`; reading only `pages` (which it never sends)
        // fell through to the hasMore guess and rendered "3 / 4" for 510 items.
        // Prefer totalPages, then pages, then derive from total/limit.
        pages:
          pg.totalPages ??
          pg.pages ??
          (pg.total ? Math.max(1, Math.ceil(pg.total / 8)) : (pg.hasMore ? mostVisitedPage + 1 : mostVisitedPage)),
        page: mostVisitedPage,
      });
    } catch (_) {
      setMostVisited([]);
    } finally {
      setMostVisitedLoading(false);
    }
  }, [api, range, member, mostVisitedPage]);

  // ── Goals stats fetch ──
  const fetchGoals = useCallback(async () => {
    setGoalsLoading(true);
    try {
      const res = await getTeamStats();
      if (res?.success && res.data) setGoals(res.data);
      else setGoals({ teamAverageScore: null, completedGoalsCount: 0, totalGoalsCount: 0, previousMonthChange: null });
    } catch (_) {
      setGoals({ teamAverageScore: null, completedGoalsCount: 0, totalGoalsCount: 0, previousMonthChange: null });
    } finally {
      setGoalsLoading(false);
    }
  }, []);

  // ── Gmail status fetch (org-wide, filter-independent) ──
  const fetchGmail = useCallback(async () => {
    setGmailLoading(true);
    try {
      const res = await getOrgGmailStatus();
      setGmail(res || null);
    } catch (_) {
      setGmail(null);
    } finally {
      setGmailLoading(false);
    }
  }, []);

  // ── Triggers ──
  useEffect(() => { fetchKpi(); }, [fetchKpi]);
  useEffect(() => { fetchTrend(); }, [fetchTrend]);
  useEffect(() => { fetchMostVisited(); }, [fetchMostVisited]);
  useEffect(() => { fetchGoals(); }, [fetchGoals]);
  useEffect(() => { fetchGmail(); }, [fetchGmail]);
  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchTeam(); }, [fetchTeam]);
  useEffect(() => { fetchTeamAll(); }, [fetchTeamAll]);

  // Reset to page 1 when filters change
  useEffect(() => { setProjectPage(1); setTeamPage(1); setMostVisitedPage(1); }, [range, member]);

  // Team Performance export — same as the legacy dashboard: fetch the team
  // analytics JSON, then build the Excel client-side (XLSX). NOT a global export.
  const exportTeam = useCallback(async () => {
    try {
      toast.loading("Preparing export…", { id: "export-toast" });
      const params = new URLSearchParams({ startDate: fmt(range.start), endDate: fmt(range.end) });
      params.append("sortOrder", teamSort.dir);
      if (member) params.append("userId", member);

      const res = await api.get(`/api/v1/live-analytics/team-analytics-export?${params.toString()}`);
      if (!res.data?.success) throw new Error(res.data?.message || "Export failed");

      const { teamAnalytics = [], exportTime } = res.data.data || {};

      // Same columns as legacy Team Performance export.
      const excelData = teamAnalytics.map((m, i) => ({
        "S.No": i + 1,
        "Member Name": `${m.firstName || ""} ${m.lastName || ""}`.trim(),
        "Email": m.email,
        "Position": m.position,
        "Active Hours": m.activeTime,
        "Idle Hours": m.idleTime,
        "Total Hours": m.hours,
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      ws["!cols"] = [{ wch: 8 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws, "Team Performance");

      const ts = new Date(exportTime || Date.now()).toISOString().replace(/[:.]/g, "-").slice(0, -5);
      XLSX.writeFile(wb, `Team_Performance_${ts}.xlsx`);

      toast.success(`Exported ${teamAnalytics.length} team members to Excel`, { id: "export-toast" });
      return true;
    } catch (err) {
      toast.error(err.message || "Failed to export team analytics", { id: "export-toast" });
      return false;
    }
  }, [api, range, member, teamSort.dir]);

  return {
    // filters — setMember is the guarded wrapper: an explicit pick (incl. the
    // "All team members" null) sticks and is never re-defaulted to self.
    range, setRange, member, setMember: selectMember, project, setProject, members, projects,
    // kpi
    kpi, kpiLoading,
    // trend
    trend, trendLoading, trendIsWeekly,
    // most visited
    mostVisited, mostVisitedMeta, mostVisitedPage, setMostVisitedPage, mostVisitedLoading,
    // goals
    goals, goalsLoading,
    // gmail
    gmail, gmailLoading,
    // projects
    projectRows, projectMeta, projectPage, setProjectPage, projectSort, setProjectSort, projectLoading,
    // team
    teamRows, teamMeta, teamPage, setTeamPage, teamSort, setTeamSort, teamLoading, exportTeam,
    teamAll, teamAllLoading,
    user,
  };
}
